import React, { useState, useEffect } from 'react';
import Modal from '../Common/Modal';
import { useAuth } from '../Auth/AuthContext';
import FileViewer from '../Common/FileViewer';
import { formatDateTimeIST } from '../../utils/dateUtils';

const LiveStampingOrderModal = ({ order, onClose, userRole }) => {
  const [activeStampTab, setActiveStampTab] = useState(0);
  const [fullOrder, setFullOrder] = useState(order);
  const [viewingFile, setViewingFile] = useState(null); // { base64, contentType, filename }

  useEffect(() => {
    setFullOrder(order);
    if (!order?.id) return;
    import('../../services/liveStamping').then(({ getOrder }) => {
      getOrder(order.id).then(res => setFullOrder(res.order || res)).catch(() => {});
    });
  }, [order?.id]);

  if (!order) return null;

  // Normalize stamps array (handle legacy single-stamp orders)
  const stamps = fullOrder.stamps || [{
    article: fullOrder.article,
    denomination: fullOrder.denomination,
    quantity: fullOrder.quantity,
    first_party: fullOrder.first_party,
    second_party: fullOrder.second_party,
    stamp_purchased_by: fullOrder.stamp_purchased_by || fullOrder.purchased_paid_by,
    stamp_paid_by: fullOrder.stamp_paid_by,
    property_description: fullOrder.property_description,
    notes: fullOrder.notes
  }];

  const currentStamp = stamps[activeStampTab] || {};

  const getStatusColor = (status) => {
    const colors = {
      placed: 'bg-blue-100 text-blue-800',
      executing: 'bg-orange-100 text-orange-800',
      preview_submitted: 'bg-violet-100 text-violet-800',
      metadata_ready: 'bg-teal-100 text-teal-800',
      compliance_check: 'bg-purple-100 text-purple-800',
      pending_verification: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      ops_review: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      payment_pending: 'bg-amber-100 text-amber-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
      if (!text) return;
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    if (!text) return null;
    return (
      <button onClick={handleCopy} className="ml-2 text-gray-400 hover:text-indigo-600" title="Copy">
        {copied ? (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    );
  };

  const InfoRow = ({ label, value, copyable = false, className = "" }) => (
    <tr className="bg-white even:bg-gray-50">
      <td className="px-4 py-2 font-medium text-gray-700 w-1/3">{label}</td>
      <td className={`px-4 py-2 text-gray-900 ${className}`}>
        <div className="flex items-center justify-between">
          <span>{value || '-'}</span>
          {copyable && value && <CopyButton text={value} />}
        </div>
      </td>
    </tr>
  );

  // Derive attached files (preview and/or final document)
  const attachedFiles = [];
  if (fullOrder.preview_base64) {
    attachedFiles.push({
      base64: fullOrder.preview_base64,
      contentType: fullOrder.preview_content_type || 'image/png',
      filename: `preview_${fullOrder.id}${(fullOrder.preview_content_type || '').includes('pdf') ? '.pdf' : '.png'}`,
      label: 'Preview Sample',
    });
  }
  if (fullOrder.document_base64) {
    attachedFiles.push({
      base64: fullOrder.document_base64,
      contentType: fullOrder.document_content_type || 'application/pdf',
      filename: `stamp_${fullOrder.id}.pdf`,
      label: 'Stamp Document',
    });
  }

  const handleDownloadFile = (file) => {
    const bytes = Uint8Array.from(atob(file.base64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: file.contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal isOpen={!!order} onClose={onClose}>
      <div className="flex flex-col h-full">

        {/* File viewer mode — replaces body */}
        {viewingFile && (
          <>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3 bg-white flex-shrink-0">
              <button onClick={() => setViewingFile(null)} className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h2 className="text-base font-bold text-gray-800">{viewingFile.label}</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              <FileViewer
                file={viewingFile.base64}
                filename={viewingFile.filename}
                onBack={() => setViewingFile(null)}
              />
            </div>
          </>
        )}

        {/* Normal detail view */}
        {!viewingFile && <>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3 bg-white flex-shrink-0">
          <button
            onClick={onClose}
            className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
            aria-label="Back"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center text-indigo-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800 flex items-center">
                {fullOrder.id}
                <CopyButton text={fullOrder.id} />
              </h2>
              <div className="flex items-center space-x-2 mt-1">
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                  {fullOrder.serviceName || fullOrder.service_name}
                </span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                  {fullOrder.state}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(fullOrder.status)}`}>
                  {fullOrder.status?.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Rejection reason banner */}
        {fullOrder.status === 'rejected' && fullOrder.rejection_reason && (
          <div className="mx-6 my-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <span className="font-semibold">Rejection reason:</span> {fullOrder.rejection_reason}
          </div>
        )}

        {/* Preview-rejection banner — order is back in executing waiting for vendor re-upload */}
        {fullOrder.status === 'executing' && fullOrder.preview_rejection_reason && (
          <div className="mx-6 my-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <span className="font-semibold">Preview rejected — please re-upload:</span> {fullOrder.preview_rejection_reason}
          </div>
        )}

        {/* Stamp Tabs (if multiple) */}
        {stamps.length > 1 && (
          <div className="px-6 py-2 bg-gray-50 border-b flex space-x-2 overflow-x-auto">
            {stamps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveStampTab(idx)}
                className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${activeStampTab === idx
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
                  }`}
              >
                Stamp {idx + 1}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Order Summary (Common) */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Order Summary
              </h4>
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-sm">
                <tbody className="divide-y divide-gray-200">
                  <InfoRow label="Request ID" value={fullOrder.id} copyable />
                  <InfoRow label="Service" value={fullOrder.serviceName || fullOrder.service_name} />
                  <InfoRow label="State" value={fullOrder.state} copyable />
                  <InfoRow label="Delivery Mode" value={fullOrder.delivery_mode} className="capitalize" />
                  {fullOrder.delivery_address && (
                    <InfoRow label="Delivery Address" value={fullOrder.delivery_address} copyable />
                  )}
                  <InfoRow label="Stamp Duty" value={`₹${((fullOrder.stamps || []).reduce((sum, s) => sum + ((s.denomination || 0) * (s.quantity || 1)), 0)).toLocaleString('en-IN')}`} className="font-bold text-green-700" />
                  <InfoRow label="Status" value={
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(fullOrder.status)}`}>
                      {fullOrder.status?.replace('_', ' ')}
                    </span>
                  } />
                  <InfoRow label="Created" value={formatDateTimeIST(fullOrder.created_at)} />
                  {fullOrder.external_ref_id && (
                    <InfoRow label="Ref ID" value={fullOrder.external_ref_id} copyable />
                  )}
                </tbody>
              </table>

              {/* Order Notes */}
              {fullOrder.order_notes && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-800 mb-1">Order Notes</p>
                  <p className="text-sm text-gray-700">{fullOrder.order_notes}</p>
                </div>
              )}

              {/* Stakeholders — admin sees both; client hides vendor; vendor hides client */}
              {(fullOrder.client_name || fullOrder.vendor_name) && userRole !== 'client' && userRole !== 'vendor' && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Stakeholders</h4>
                  <div className="bg-gray-50 p-3 rounded-lg border text-sm">
                    <p><span className="font-medium">Client:</span> {fullOrder.client_name}</p>
                    <p><span className="font-medium">Vendor:</span> {fullOrder.vendor_name || 'Unassigned'}</p>
                  </div>
                </div>
              )}

              {/* Certificate Details — hidden for client until approved (completed) */}
              {(userRole !== 'client' || ['metadata_ready', 'compliance_check', 'pending_verification', 'completed'].includes(fullOrder.status)) && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    Certificate Details
                  </h4>
                  <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-sm">
                    <tbody className="divide-y divide-gray-200">
                      <tr className="bg-white even:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-700 w-1/3">Certificate ID</td>
                        <td className="px-4 py-2 text-gray-900">
                          {fullOrder.stamp_metadata?.certificate_number ? (
                            <div className="flex items-center justify-between">
                              <span>{fullOrder.stamp_metadata.certificate_number}</span>
                              <CopyButton text={fullOrder.stamp_metadata.certificate_number} />
                            </div>
                          ) : (
                            <span className="text-gray-400 italic text-xs">Procurement in progress</span>
                          )}
                        </td>
                      </tr>
                      <tr className="bg-white even:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-700 w-1/3">Issued On</td>
                        <td className="px-4 py-2 text-gray-900">
                          {fullOrder.stamp_metadata?.issue_date ? (
                            <div className="flex items-center justify-between">
                              <span>{formatDateTimeIST(fullOrder.stamp_metadata.issue_date)}</span>
                              <CopyButton text={formatDateTimeIST(fullOrder.stamp_metadata.issue_date)} />
                            </div>
                          ) : (
                            <span className="text-gray-400 italic text-xs">Procurement in progress</span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Current Stamp Details */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Stamp {stamps.length > 1 ? `#${activeStampTab + 1}` : 'Details'}
              </h4>
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-sm mb-4">
                <tbody className="divide-y divide-gray-200">
                  <InfoRow label="Article" value={currentStamp.article} copyable />
                  <InfoRow label="Denomination" value={currentStamp.denomination ? `₹${currentStamp.denomination}` : '-'} copyable />
                  <InfoRow label="Quantity" value={currentStamp.quantity} />
                  <InfoRow label="Purchased By" value={currentStamp.stamp_purchased_by} copyable />
                  <InfoRow label="Paid By" value={currentStamp.stamp_paid_by} copyable />
                  {currentStamp.property_description && <InfoRow label="Property Description" value={currentStamp.property_description} />}
                  {currentStamp.notes && <InfoRow label="Notes" value={currentStamp.notes} />}
                </tbody>
              </table>

              {/* Party Details */}
              <div className="space-y-3">
                {/* First Party */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="font-medium text-blue-800 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    First Party
                  </p>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium text-gray-600">Name:</span> {currentStamp.first_party?.name || '-'}</p>
                    <p><span className="font-medium text-gray-600">Mobile:</span> {currentStamp.first_party?.mobile || '-'}</p>
                    <p><span className="font-medium text-gray-600">Address:</span> {currentStamp.first_party?.address || '-'}</p>
                  </div>
                </div>

                {/* Second Party */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="font-medium text-green-800 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Second Party
                  </p>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium text-gray-600">Name:</span> {currentStamp.second_party?.name || '-'}</p>
                    <p><span className="font-medium text-gray-600">Mobile:</span> {currentStamp.second_party?.mobile || '-'}</p>
                    <p><span className="font-medium text-gray-600">Address:</span> {currentStamp.second_party?.address || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Attached files */}
        {attachedFiles.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Attached Files</h3>
            <div className="space-y-2">
              {attachedFiles.map(file => (
                <div key={file.filename} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">{file.label}</span>
                    <span className="text-xs text-gray-400">{file.filename}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setViewingFile(file)}
                      className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDownloadFile(file)}
                      className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center flex-shrink-0">
          <p className="text-xs text-gray-500">
            {stamps.length} stamp{stamps.length > 1 ? 's' : ''} in this order
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
          >
            Close
          </button>
        </div>
        </>}
      </div>
    </Modal>
  );
};

export default LiveStampingOrderModal;
